import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { iconsPath } from '../../../utils/imagePath';
import { GridOption } from '../../GridAssets/GridAssets';
import { useNavigate } from 'react-router-dom';
import { SaveProjectConfirmationModal } from '../../GridAssets/GridAssetsModal';
import ConfirmationModel from '../../ConfirmationModel';
import { useSelector } from 'react-redux';
import store from '../../../redux/store';
import { useAppLeftPanel } from './AppLeftSection/hooks/useAppLeftPanel';
import { useDragDropUtils } from './AppLeftSection/utils/dragDropUtils';
import { useHierarchyUtils } from './AppLeftSection/utils/hierarchyUtils';
import { useSceneUtils } from './AppLeftSection/utils/sceneUtils';
import useClickOutside from '../../UseClickOutside';
import { lightDataUpdate } from '../../../redux/Slice/gridSlice';
import { copyObject } from '../../../redux/Slice/globalStates';
import {
  selectObjectInScene,
  serializeSceneState,
  DeleteMesh,
} from '../../../pages/Dashboard/GridPage';
import { reconcileAssetsDataFromScene } from '../../../utils/gridHistoryAssetsSync';
import {
  assetIcons,
  LightInitialData,
  shapesIcon,
  windowTabs,
} from '../../../utils/constant';
import { PreviewLeftComponent } from '../../../utils/gridUtils';
import { exitPreviewMode } from '../../../utils/preview/exitPreviewMode';
import { findItem } from '../../Copy-Paste';
import { extractMeshesFromGLB } from '../../gridpagefunction/MeshExtractation';

// Import the new sub-components
import LeftPanelHeader from './AppLeftSection/components/LeftPanelHeader';
import TabContainer from './AppLeftSection/components/TabContainer';
import AssetsList from './AppLeftSection/components/AssetList';

const AppLeftPanel = React.memo(
  forwardRef(
    (
      {
        name,
        setName,
        setPublished,
        lastSavedSceneSnapshot,
        setSelectedObjectState,
        objectt,
        isAnimationMinimized,
        animationHeight = 250,
        isAnimationResizing = false,
        published,
        category,
      },
      ref
    ) => {
      const { selectedObject, scene, transformControls } = objectt;
      const { animationObjects } = useSelector((state) => state.animation);

      // Additional state for features not in the hook (defined early)
      const [hierarchyUpdateTrigger, setHierarchyUpdateTrigger] = useState(0);

      // Use custom hooks for state management and utilities
      const {
        // State
        selectedPop,
        setselectedPop,
        collapsedItems,
        setCollapsedItems,
        isPopupOpen,
        setIsPopupOpen,
        projectConfirmation,
        setProjectConfirmation,
        children,
        setChildren,
        lastSaved,
        setLastSaved,
        isRenaming,
        setIsRenaming,
        newName,
        setNewName,
        submenuPosition,
        setSubmenuPosition,
        renamingItem,
        setRenamingItem,
        expandedGLBs,
        setExpandedGLBs,
        glbMeshes,
        setGLBMeshes,
        selectedGLBMeshPop,
        setSelectedGLBMeshPop,
        glbMeshSubmenuPosition,
        setGLBMeshSubmenuPosition,
        selectedLightPop,
        setSelectedLightPop,
        isRenamingLight,
        setIsRenamingLight,
        renamingLight,
        setRenamingLight,
        newLightName,
        setNewLightName,
        lightSubmenuPosition,
        setLightSubmenuPosition,
        showLightDeleteConfirmation,
        setShowLightDeleteConfirmation,
        lightToDelete,
        setLightToDelete,
        copiedItem,
        setCopiedItem,
        selectedCameraPop,
        setSelectedCameraPop,
        cameraSubmenuPosition,
        setCameraSubmenuPosition,
        showCameraDeleteConfirmation,
        setShowCameraDeleteConfirmation,
        cameraToDelete,
        setCameraToDelete,
        cameraRemoved,
        setCameraRemoved,
        draggingAssetId,
        setDraggingAssetId,
        hoverTargetAssetId,
        setHoverTargetAssetId,
        collapsedMap,
        setCollapsedMap,
        draggedFileName,
        setDraggedFileName,

        // Functions
        stopAllAnimations,
        startAllAnimations,
        handleRemoveLight,
        confirmLightDeletion,
        handleRemoveCamera,
        confirmCameraDeletion,
        handleRemoveParticle,
        handleSelectParticle,
        toggleCollapsed,
        handleTogglePopup,
        getAssetId,
        getObjectByAsset,
        allAssets,
        showParticleDeleteConfirmation,
        setShowParticleDeleteConfirmation,
        particleToDelete,
        setParticleToDelete,
        confirmParticleDeletion,

        // Redux state
        assets_data,
        select_object,
        light_data,
        camera_properties,
        particles,
        selectedLightId,
        dispatch,
      } = useAppLeftPanel({
        scene,
        selectedObject,
        setSelectedObjectState,
        objectt,
        lastSavedSceneSnapshot,
        transformControls,
      });

      // Use hierarchy utilities first
      const {
        reconstructParentChildRelationships,
        findDirectChildrenAssets,
        renderChildren,
      } = useHierarchyUtils({
        scene,
        assets_data,
        light_data,
        camera_properties,
        particles,
        allAssets,
      });

      // Force update callback for immediate UI refresh
      const forceUpdate = useCallback(() => {
        setChildren([...scene.children]);
        setHierarchyUpdateTrigger((prev) => prev + 1);
      }, [scene, setHierarchyUpdateTrigger]);

      // Use drag and drop utilities (after hierarchy utils)
      const { handleDropOnAsset, handleDropToRoot } = useDragDropUtils({
        scene,
        assets_data,
        light_data,
        particles,
        dispatch,
        setSelectedObjectState,
        objectt,
        reconstructParentChildRelationships,
        setHierarchyUpdateTrigger,
        allAssets,
        forceUpdate,
      });

      // Use scene utilities
      const { isSceneModified } = useSceneUtils();

      const { window_type } = useSelector((state) => state.window);

      // Expose functions to parent component
      useImperativeHandle(ref, () => ({
        stopAllAnimations,
        startAllAnimations,
      }));

      const outSideRef = useRef();
      const navigate = useNavigate();

      // Core component state and effects
      const memoizedExtractMeshesFromGLB = useCallback(
        (glbAsset) => extractMeshesFromGLB(glbAsset, scene),
        [scene]
      );


      useClickOutside(outSideRef, () => {
        setselectedPop('');
        setSelectedLightPop(null);
        setSelectedGLBMeshPop(null);
        setSelectedCameraPop(null);
      });



      const handleSave = () => {
        setProjectConfirmation(false);
      };

      const handleNavigateBack = () => {
        if (window_type === 'preview') {
          exitPreviewMode({
            dispatch: store.dispatch,
            scene,
            currentWindowType: window_type,
          });
          return;
        }

        // Always show confirmation modal when back is clicked — whether scene
        // has assets or not, so the user consciously chooses to save or discard.
        setProjectConfirmation(true);
      };

      // Undo/redo: refresh hierarchy tree, selection highlights, and scene children.
      useEffect(() => {
        const syncLeftPanelAfterHistory = () => {
          const { assets_object, particles } = store.getState();
          reconstructParentChildRelationships(
            scene,
            assets_object.assets_data,
            assets_object.light_data,
            assets_object.camera_properties,
            particles?.particles
          );
          forceUpdate();
        };

        window.addEventListener('gridHistoryUiRefresh', syncLeftPanelAfterHistory);
        window.addEventListener('gridHistoryRestored', syncLeftPanelAfterHistory);
        return () => {
          window.removeEventListener(
            'gridHistoryUiRefresh',
            syncLeftPanelAfterHistory
          );
          window.removeEventListener(
            'gridHistoryRestored',
            syncLeftPanelAfterHistory
          );
        };
      }, [scene, forceUpdate, reconstructParentChildRelationships]);

      // If grid has assets but Redux list is empty, rebuild the left-panel list.
      useEffect(() => {
        if (!scene) return;

        const data = store.getState().assets_object?.assets_data || {};
        const listedCount = [
          'mesh',
          'image',
          'video',
          'audio',
          'text',
          'hotspot',
          'empty',
        ].reduce((sum, key) => sum + (data[key]?.length || 0), 0);

        if (listedCount > 0) return;

        let hasRootSceneAsset = false;
        scene.traverse((obj) => {
          if (hasRootSceneAsset || !obj?.deleteType) return;
          if (!['mesh', 'image', 'video', 'audio', 'text', 'hotspot', 'empty'].includes(obj.deleteType)) {
            return;
          }
          if (!obj.parent || obj.parent === scene) {
            hasRootSceneAsset = true;
          }
        });

        if (hasRootSceneAsset) {
          reconcileAssetsDataFromScene(scene);
          forceUpdate();
        }
      }, [scene, assets_data, hierarchyUpdateTrigger, forceUpdate]);

      // Key effectsstartAllAnimations
      useEffect(() => {
        setChildren([...scene.children]);
      }, [hierarchyUpdateTrigger, scene]);

      useEffect(() => {
        setLastSaved(lastSavedSceneSnapshot);
      }, [lastSavedSceneSnapshot]);

      const reconstructionDone = useRef(false);
      useEffect(() => {
        if (published) {
          const currentSceneState = serializeSceneState();
          setLastSaved(currentSceneState);
        }
      }, [published]);

      useEffect(() => {
        if (
          !reconstructionDone.current &&
          scene &&
          assets_data &&
          Object.keys(assets_data).length > 0
          // light_data &&
          // light_data.length > 0
        ) {
          const timeout = setTimeout(() => {
            reconstructParentChildRelationships(
              scene,
              assets_data,
              light_data,
              camera_properties
            );
            reconstructionDone.current = true;
            setChildren([...scene.children]);
          }, 500);

          return () => clearTimeout(timeout);
        }
      }, [scene, assets_data, light_data, reconstructParentChildRelationships]);

      // NOTE: reconstruction after publish is handled by the parent component
      // (GridPage) which will remount this component when a full refresh is
      // required. Keeping reconstruction here caused duplicate runs; removed
      // the local event listener to avoid double execution.

      // Copy-paste keyboard handlers
      // Note: We only handle Ctrl+C here. Ctrl+V is handled by useTransformControlsHotkeys
      // to avoid double duplication
      useEffect(() => {
        const handleKeyDown = (event) => {
          // Disable copy shortcuts in preview mode
          const window_type = store.getState()?.window?.window_type;
          if (window_type === 'preview') return;

          if (event.ctrlKey || event.metaKey) {
            if (event.key === 'c') {
              const item = findItem(selectedObject, {
                light_data,
                assets_data,
                particles,
              });
              if (item) {
                setCopiedItem(item);
                // Also store in Redux for useTransformControlsHotkeys
                store.dispatch(copyObject(selectedObject));
              }
            }
            // Removed Ctrl+V handler here - it's handled by useTransformControlsHotkeys
            // to prevent double duplication
          }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
      }, [selectedObject, copiedItem]);

      return (
        <div
          className={`left-panel scrollbar-none  ${window_type === 'design' ? 'h-full' : ''}`}
          onScroll={(e) => {
            e.currentTarget.style.setProperty('--hierarchy-scroll-left', `${e.currentTarget.scrollLeft}px`);
          }}
          style={{
            height: isAnimationMinimized
              ? 'calc(100% - 40px)'
              : `calc(100% - ${animationHeight - 6}px)`,
            transition: isAnimationResizing ? 'none' : 'height 0.3s ease-in-out',
            ...(window_type === 'preview' && { overflowY: 'visible', overflowX: 'visible' }),
          }}
        >
          <LeftPanelHeader
            name={name}
            setName={setName}
            handleNavigateBack={handleNavigateBack}
            handleTogglePopup={handleTogglePopup}
            iconsPath={iconsPath}
            category={category || objectt?.category}
          />

          <img className="left-divider" src={iconsPath?.line4Image} alt="" />

          <TabContainer
            windowTabs={windowTabs}
            window_type={window_type}
            store={store} 
            objectt={objectt}
            scene={scene}
            startAllAnimations={startAllAnimations}
          />

          <img className="left-divider" src={iconsPath?.line5Image} alt="" />

          {window_type === 'design' ? (
            <>
              <AssetsList
                scene={scene}
                assets_data={assets_data}
                light_data={light_data}
                camera_properties={camera_properties}
                particles={particles}
                select_object={select_object}
                selectedObject={selectedObject}
                selectedLightId={selectedLightId}
                expandedGLBs={expandedGLBs}
                setExpandedGLBs={setExpandedGLBs}
                glbMeshes={glbMeshes}
                setGLBMeshes={setGLBMeshes}
                selectedPop={selectedPop}
                setselectedPop={setselectedPop}
                allAssets={allAssets}
                selectedLightPop={selectedLightPop}
                setSelectedLightPop={setSelectedLightPop}
                selectedGLBMeshPop={selectedGLBMeshPop}
                setSelectedGLBMeshPop={setSelectedGLBMeshPop}
                selectedCameraPop={selectedCameraPop}
                setSelectedCameraPop={setSelectedCameraPop}
                submenuPosition={submenuPosition}
                setSubmenuPosition={setSubmenuPosition}
                lightSubmenuPosition={lightSubmenuPosition}
                setLightSubmenuPosition={setLightSubmenuPosition}
                glbMeshSubmenuPosition={glbMeshSubmenuPosition}
                setGLBMeshSubmenuPosition={setGLBMeshSubmenuPosition}
                cameraSubmenuPosition={cameraSubmenuPosition}
                setCameraSubmenuPosition={setCameraSubmenuPosition}
                isRenaming={isRenaming}
                setIsRenaming={setIsRenaming}
                renamingItem={renamingItem}
                setRenamingItem={setRenamingItem}
                newName={newName}
                setNewName={setNewName}
                isRenamingLight={isRenamingLight}
                setIsRenamingLight={setIsRenamingLight}
                renamingLight={renamingLight}
                setRenamingLight={setRenamingLight}
                newLightName={newLightName}
                setNewLightName={setNewLightName}
                draggingAssetId={draggingAssetId}
                setDraggingAssetId={setDraggingAssetId}
                hoverTargetAssetId={hoverTargetAssetId}
                setHoverTargetAssetId={setHoverTargetAssetId}
                collapsedMap={collapsedMap}
                toggleCollapsed={toggleCollapsed}
                cameraRemoved={cameraRemoved}
                objectt={objectt}
                store={store}
                dispatch={dispatch}
                selectObjectInScene={selectObjectInScene}
                setSelectedObjectState={setSelectedObjectState}
                handleDropOnAsset={handleDropOnAsset}
                handleDropToRoot={handleDropToRoot}
                findDirectChildrenAssets={findDirectChildrenAssets}
                renderChildren={renderChildren}
                handleRemoveLight={handleRemoveLight}
                handleRemoveParticle={handleRemoveParticle}
                handleRemoveCamera={handleRemoveCamera}
                handleSelectParticle={handleSelectParticle}
                memoizedExtractMeshesFromGLB={memoizedExtractMeshesFromGLB}
                outSideRef={outSideRef}
                iconsPath={iconsPath}
                shapesIcon={shapesIcon}
                assetIcons={assetIcons}
                hierarchyUpdateTrigger={hierarchyUpdateTrigger}
                DeleteMesh={DeleteMesh}
              />
              {isPopupOpen && <GridOption setIsPopupOpen={setIsPopupOpen} />}

              {projectConfirmation && (
                <SaveProjectConfirmationModal
                  isOpen={projectConfirmation}
                  onClose={() => setProjectConfirmation(false)}
                  setProjectConfirmation={setProjectConfirmation}
                  onSave={handleSave}
                  setPublished={setPublished}
                  name={name}
                  setName={setName}
                  scene={scene}
                  transformControls={transformControls}
                />
              )}

              {showLightDeleteConfirmation && (
                <ConfirmationModel
                  isOpen={showLightDeleteConfirmation}
                  onClose={() => {
                    setShowLightDeleteConfirmation(false);
                    setLightToDelete(null);
                  }}
                  onConfirm={confirmLightDeletion}
                />
              )}

              {showParticleDeleteConfirmation && (
                <ConfirmationModel
                  isOpen={showParticleDeleteConfirmation}
                  onClose={() => {
                    setShowParticleDeleteConfirmation(false);
                    setParticleToDelete(null);
                  }}
                  onConfirm={confirmParticleDeletion}
                />
              )}

              {showCameraDeleteConfirmation && (
                <ConfirmationModel
                  isOpen={showCameraDeleteConfirmation}
                  onClose={() => {
                    setShowCameraDeleteConfirmation(false);
                    setCameraToDelete(null);
                  }}
                  onConfirm={confirmCameraDeletion}
                />
              )}
            </>
          ) : window_type === 'preview' ? (
            <PreviewLeftComponent
              objectt={{
                ...objectt,
                stopAllAnimations,
                restartAnimations: startAllAnimations,
                startAllAnimations,
              }}
            />
          ) : (
            ''
          )}

          {draggedFileName && (
            <div className="dragged-file-name">Dragging: {draggedFileName}</div>
          )}

          <style>
            {`
          .dragged-file-name {
            position: fixed;
            bottom: 10px;
            left: 10px;
            background: rgba(0, 0, 0, 0.7);
            color: white;
            padding: 5px 10px;
            border-radius: 5px;
            z-index: 1000;
          }
        `}
          </style>
        </div>
      );
    }
  )
);

export default AppLeftPanel;
